import markGreen from '@masar/assets/img/mark_green.png';
import markLight from '@masar/assets/img/mark_light.png';
import { loginUrl, useLogout, usePublicCatalog } from '@masar/api-client/query';
import { formatPercentBp } from '@masar/i18n';
import { useI18n } from '@masar/i18n/react';
import { Icon, cx, useToast } from '@masar/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { usePrefs } from '../app/locale';
import { NAV, NAV_GROUP_LABEL, type NavGroup } from '../app/nav';
import { hasAny } from '../app/permissions';
import { useMeStrict } from '../app/session';
import { publicSiteOrigin } from '../app/site';
import { useNavBadges } from './badges';
import s from './Shell.module.css';

const PUBLIC_SITE = publicSiteOrigin(import.meta.env.VITE_PUBLIC_SITE_URL, window.location);

export function Shell({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n();
  const { setLocale, theme, setTheme } = usePrefs();
  const me = useMeStrict();
  const badges = useNavBadges();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const logout = useLogout();
  const toast = useToast();
  const mainRef = useRef<HTMLElement>(null);
  const season = usePublicCatalog().data?.season;

  const items = NAV.filter((n) => hasAny(me.permissions, n.perms));
  const current = items.find((n) => loc.pathname === n.path || loc.pathname.startsWith(`${n.path}/`));
  const anyBadge = Object.values(badges).some(Boolean);
  const roleName = locale === 'en' ? me.role.name.en || me.role.name.ar : me.role.name.ar;

  // Close the phone menu on navigation; move focus to the page for screen readers.
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const onLogout = () => {
    logout.mutate(undefined, {
      onSuccess: (r) => {
        window.location.assign(r.end_session_url ?? '/login');
      },
      onError: () => toast.show(t('تعذّر تسجيل الخروج. حاول مجدداً.'), { tone: 'crit' }),
    });
  };

  const group = (g: NavGroup) => {
    const list = items.filter((n) => n.group === g);
    if (!list.length) return null;
    return (
      <>
        <p className={s.navG}>{t(NAV_GROUP_LABEL[g])}</p>
        {list.map((n) => (
          <NavLink key={n.key} to={n.path} data-label={t(n.label)} aria-label={t(n.label)}>
            <Icon name={n.icon} />
            <span className={s.nl}>{t(n.label)}</span>
            {badges[n.key] ? <span className={cx(s.badge, 'num')}>{badges[n.key]}</span> : null}
          </NavLink>
        ))}
      </>
    );
  };

  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <div className={s.ws}>
      <a className={s.skip} href="#main">
        {t('انتقل إلى المحتوى')}
      </a>
      <aside className={cx(s.side, open && s.open)} aria-label={t('القائمة')}>
        <div className={s.topRow} />
        <button
          type="button"
          className={s.menuBtn}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t('إغلاق القائمة') : t('القائمة')}
          aria-expanded={open}
        >
          <Icon name={open ? 'x' : 'menu'} />
          {!open && anyBadge ? <i className={s.dot} /> : null}
        </button>
        <span className={s.topTitle}>{current ? t(current.label) : ''}</span>
        <div className={s.logo}>
          <img className="lg-light" src={markGreen} alt={t('مسار EV')} width={121} height={32} />
          <img className="lg-dark" src={markLight} alt={t('مسار EV')} width={121} height={32} />
        </div>
        <div className={s.who}>
          <span className={s.av} aria-hidden="true">
            {me.name.trim()[0] ?? '·'}
          </span>
          <div>
            <b>{me.name}</b>
            <small>
              {roleName}
              {me.is_owner ? ` · ${t('كل الصلاحيات')}` : ''}
            </small>
          </div>
        </div>
        <nav className={s.nav} aria-label={t('أقسام لوحة العمل')}>
          {group('ops')}
          {group('mgmt')}
        </nav>
        <div className={s.foot}>
          <div className={s.footLine}>
            <Icon name="bolt" size="sm" />{' '}
            {season
              ? `${t('تسعير الموسم مفعّل')} +${formatPercentBp(season.uplift_bp)}`
              : t('التسعير العادي')}
          </div>
          <a className={s.siteLink} href={`${PUBLIC_SITE}/${locale}`}>
            <Icon name="home" size="sm" /> {t('موقع الحجز')}
          </a>
          <button
            type="button"
            className={s.siteLink}
            onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
            lang={locale === 'en' ? 'ar' : 'en'}
          >
            <Icon name="globe" size="sm" /> {locale === 'en' ? 'العربية' : 'English'}
          </button>
          <button
            type="button"
            className={s.siteLink}
            onClick={() => setTheme(nextTheme)}
            aria-pressed={theme === 'dark'}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size="sm" />{' '}
            {theme === 'dark' ? t('الوضع الفاتح') : t('الوضع الداكن')}
          </button>
          <button type="button" className={s.siteLink} onClick={onLogout} disabled={logout.isPending}>
            <Icon name="logout" size="sm" /> {t('تسجيل الخروج')}
          </button>
        </div>
      </aside>
      <main id="main" ref={mainRef} className={s.main} tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}

/** Full-page panel (sign-in, invite, errors) with the Masar mark. */
export function CenterPanel({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className={s.center}>
      <div className={s.panel}>
        <img className={cx(s.panelLogo, 'lg-light')} src={markGreen} alt={t('مسار EV')} />
        <img className={cx(s.panelLogo, 'lg-dark')} src={markLight} alt={t('مسار EV')} />
        {children}
      </div>
    </div>
  );
}

export { loginUrl };
